'use client';
import React, { useState } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { ChevronDown, Palette, Package, FileText, Rocket } from 'lucide-react';

const CATEGORY_ICONS = { brand: Palette, products: Package, content: FileText, launch: Rocket };
const CATEGORY_COLORS = {
  brand: { text: 'text-accent', bg: 'bg-accent/10', border: 'border-accent/20', activeBg: 'bg-accent/15', activeBorder: 'border-accent/40' },
  products: { text: 'text-mint', bg: 'bg-mint/10', border: 'border-mint/20', activeBg: 'bg-mint/15', activeBorder: 'border-mint/40' },
  content: { text: 'text-pink-400', bg: 'bg-pink-400/10', border: 'border-pink-400/20', activeBg: 'bg-pink-400/15', activeBorder: 'border-pink-400/40' },
  launch: { text: 'text-orange-400', bg: 'bg-orange-400/10', border: 'border-orange-400/20', activeBg: 'bg-orange-400/15', activeBorder: 'border-orange-400/40' },
};

type CategoryKey = 'brand' | 'products' | 'content' | 'launch';

export default function WhatAIMakerCreates() {
  const { t } = useLanguage();
  const wc = (t as any)?.whatAIMakerCreates;
  const [openCategory, setOpenCategory] = useState<CategoryKey | null>('brand');

  const categories: { key: CategoryKey; label: string; desc: string; items: { title: string; desc: string; emoji: string }[] }[] = [
    {
      key: 'brand',
      label: wc?.brandLabel ?? 'Brand',
      desc: wc?.brandDesc ?? 'Your complete visual and verbal identity — name, colors, personality, and style.',
      items: [
        { title: wc?.brand1Title ?? 'Logo Concepts', desc: wc?.brand1Desc ?? 'Visual logo directions and style boards for your brand', emoji: '🎨' },
        { title: wc?.brand2Title ?? 'Color Palettes', desc: wc?.brand2Desc ?? 'Curated color systems that match your brand personality', emoji: '🖌️' },
        { title: wc?.brand3Title ?? 'Brand Style Boards', desc: wc?.brand3Desc ?? 'Mood boards, typography pairings, and visual direction', emoji: '✨' },
      ],
    },
    {
      key: 'products',
      label: wc?.productsLabel ?? 'Products',
      desc: wc?.productsDesc ?? 'Your product line, pricing, packaging, and service offerings — fully defined.',
      items: [
        { title: wc?.products1Title ?? 'Product Mockups', desc: wc?.products1Desc ?? 'Visual concepts for your physical or digital products', emoji: '📦' },
        { title: wc?.products2Title ?? 'Menu Examples', desc: wc?.products2Desc ?? 'Service menus, pricing tiers, and package structures', emoji: '📋' },
        { title: wc?.products3Title ?? 'Service Card Examples', desc: wc?.products3Desc ?? 'Professional service cards and offering descriptions', emoji: '💳' },
      ],
    },
    {
      key: 'content',
      label: wc?.contentLabel ?? 'Content',
      desc: wc?.contentDesc ?? 'A full content strategy with post ideas, reels, stories, and ad concepts.',
      items: [
        { title: wc?.content1Title ?? 'Instagram Post Examples', desc: wc?.content1Desc ?? 'Captions, hashtags, and visual post concepts', emoji: '📸' },
        { title: wc?.content2Title ?? 'Reel Concepts', desc: wc?.content2Desc ?? 'Short-form video scripts and scene-by-scene breakdowns', emoji: '🎬' },
        { title: wc?.content3Title ?? 'Story Layouts', desc: wc?.content3Desc ?? 'Story sequences and interactive content ideas', emoji: '📱' },
        { title: wc?.content4Title ?? 'Ad Concepts', desc: wc?.content4Desc ?? 'Paid ad copy, hooks, and creative direction', emoji: '📣' },
      ],
    },
    {
      key: 'launch',
      label: wc?.launchLabel ?? 'Launch',
      desc: wc?.launchDesc ?? 'Everything you need to go live — flyers, campaigns, and a 90-day roadmap.',
      items: [
        { title: wc?.launch1Title ?? 'Flyer Concepts', desc: wc?.launch1Desc ?? 'Print and digital flyer layouts for your launch event', emoji: '📄' },
        { title: wc?.launch2Title ?? 'Campaign Examples', desc: wc?.launch2Desc ?? 'Multi-channel campaign strategies and messaging', emoji: '🚀' },
        { title: wc?.launch3Title ?? 'Landing Page Inspiration', desc: wc?.launch3Desc ?? 'Page structure, copy, and conversion-focused layouts', emoji: '🌐' },
      ],
    },
  ];

  const toggle = (key: CategoryKey) => {
    setOpenCategory(prev => (prev === key ? null : key));
  };

  return (
    <section className="py-20 px-6 lg:px-10 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute bottom-0 left-1/4 w-96 h-64 bg-primary/4 rounded-full blur-3xl" />
      </div>

      <div className="max-w-5xl mx-auto relative">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="inline-block text-xs font-700 uppercase tracking-widest text-mint mb-3 px-3 py-1 rounded-full bg-mint/10 border border-mint/20">
            {wc?.sectionLabel ?? 'What AIMaker Creates'}
          </span>
          <h2 className="text-3xl md:text-4xl font-800 text-foreground mb-4">
            {wc?.sectionTitle ?? 'One Dream,'}{' '}
            <span className="gradient-text">{wc?.sectionTitleHighlight ?? 'Everything You Need'}</span>
          </h2>
          <p className="text-base text-muted-foreground max-w-2xl mx-auto">
            {wc?.sectionSubtitle ?? 'AIMaker generates a complete set of brand, product, content, and launch assets — all from a single idea.'}
          </p>
        </div>

        {/* Accordion categories */}
        <div className="space-y-3">
          {categories.map((cat) => {
            const color = CATEGORY_COLORS[cat.key];
            const CatIcon = CATEGORY_ICONS[cat.key];
            const isOpen = openCategory === cat.key;

            return (
              <div
                key={cat.key}
                className={`rounded-2xl border transition-all duration-300 overflow-hidden ${
                  isOpen ? `${color.activeBg} ${color.activeBorder}` : 'glass-card border-border hover:border-primary/20'
                }`}
              >
                {/* Accordion header */}
                <button
                  onClick={() => toggle(cat.key)}
                  className="w-full flex items-center justify-between p-5 text-left group"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-xl ${color.bg} border ${color.border} flex items-center justify-center flex-shrink-0 transition-transform duration-200 ${isOpen ? 'scale-110' : 'group-hover:scale-105'}`}>
                      <CatIcon size={18} className={color.text} />
                    </div>
                    <div>
                      <h3 className="text-base font-700 text-foreground">{cat.label}</h3>
                      <p className="text-sm text-muted-foreground mt-0.5 leading-snug">{cat.desc}</p>
                    </div>
                  </div>
                  <ChevronDown
                    size={18}
                    className={`text-muted-foreground flex-shrink-0 ml-4 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {/* Expanded content */}
                {isOpen && (
                  <div className="px-5 pb-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pt-2 border-t border-border/50">
                      {cat.items.map((item, idx) => (
                        <div
                          key={idx}
                          className={`rounded-xl ${color.bg} border ${color.border} p-4 transition-all duration-200 hover:scale-[1.02]`}
                        >
                          <div className="text-2xl mb-2">{item.emoji}</div>
                          <h4 className={`text-sm font-700 ${color.text} mb-1`}>{item.title}</h4>
                          <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
                          {/* Visual preview placeholder */}
                          <div className={`mt-3 rounded-lg ${color.bg} border ${color.border} border-dashed p-2 flex items-center justify-center`}>
                            <span className="text-xs text-muted-foreground/60 font-500">
                              {wc?.previewLabel ?? 'AI Visual Preview Coming Soon'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
